import React, { useState, useRef, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Paper,
} from '@mui/material';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { scanDocument } from '../../services/aiService';

/**
 * DocumentScanButton - A reusable component for scanning driver's licenses and insurance cards
 * 
 * @param {Object} props
 * @param {'drivers_license' | 'insurance_card'} props.documentType - Type of document to scan
 * @param {function} props.onDataExtracted - Callback function called with extracted data
 * @param {string} props.buttonLabel - Custom button label (optional)
 * @param {string} props.buttonVariant - MUI button variant (optional, default: 'outlined')
 * @param {string} props.buttonSize - MUI button size (optional, default: 'small')
 * @param {boolean} props.disabled - Whether the button is disabled
 */
const DocumentScanButton = ({
  documentType,
  onDataExtracted,
  buttonLabel,
  buttonVariant = 'outlined',
  buttonSize = 'small',
  disabled = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [confidence, setConfidence] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const defaultLabel = documentType === 'drivers_license' 
    ? 'Scan License' 
    : 'Scan Insurance';

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setError(null);
    setExtractedData(null);
    setConfidence(null);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleCloseDialog = () => {
    stopCamera();
    setDialogOpen(false);
    setActiveTab(0);
    setSelectedImage(null);
    setImagePreview(null);
    setError(null);
    setExtractedData(null);
    setConfidence(null);
  };

  const handleTabChange = (event, newValue) => {
    if (newValue === 1) {
      startCamera();
    } else {
      stopCamera();
    }
    setActiveTab(newValue);
    setSelectedImage(null);
    setImagePreview(null);
    setError(null);
  };

  // File upload handler
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      // Extract base64 without the data URI prefix
      const base64 = e.target.result.split(',')[1];
      setSelectedImage({
        base64,
        mimeType: file.type,
      });
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Unable to access camera. Please check permissions or use file upload instead.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setImagePreview(dataUrl);
    setSelectedImage({
      base64: dataUrl.split(',')[1],
      mimeType: 'image/jpeg',
    });
    setError(null);
  }, []);

  // Process the image with OCR
  const handleProcess = async () => {
    if (!selectedImage) {
      setError('Please select or capture an image first');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await scanDocument(
        selectedImage.base64,
        documentType,
        selectedImage.mimeType
      );

      setExtractedData(result.data);
      setConfidence(result.confidence);
    } catch (err) {
      console.error('OCR processing error:', err);
      setError(err.message || 'Failed to process document. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Apply the extracted data
  const handleApply = () => {
    if (extractedData && onDataExtracted) {
      onDataExtracted(extractedData, confidence);
    }
    handleCloseDialog();
  };

  // Render extracted data preview
  const renderDataPreview = () => {
    if (!extractedData) return null;

    const fields = documentType === 'drivers_license'
      ? [
          { key: 'firstName', label: 'First Name' },
          { key: 'lastName', label: 'Last Name' },
          { key: 'dateOfBirth', label: 'Date of Birth' },
          { key: 'sex', label: 'Sex' },
          { key: 'address', label: 'Address' },
          { key: 'city', label: 'City' },
          { key: 'state', label: 'State' },
          { key: 'zipCode', label: 'ZIP Code' },
          { key: 'licenseNumber', label: 'License Number' },
          { key: 'expirationDate', label: 'Expiration Date' },
        ]
      : [
          { key: 'insurerName', label: 'Insurance Company' },
          { key: 'memberId', label: 'Member ID' },
          { key: 'groupNumber', label: 'Group Number' },
          { key: 'planName', label: 'Plan Name' },
          { key: 'subscriberName', label: 'Subscriber Name' },
          { key: 'effectiveDate', label: 'Effective Date' },
          { key: 'copayPrimaryCare', label: 'Copay (Primary Care)' },
          { key: 'rxBin', label: 'Rx BIN' },
          { key: 'rxPcn', label: 'Rx PCN' },
        ];

    return (
      <Box sx={{ mt: 2 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            backgroundColor: 'rgba(76, 175, 80, 0.08)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CheckCircleIcon sx={{ color: '#4caf50' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
              Extracted Data
            </Typography>
            <Chip 
              label={`${Math.round((confidence || 0) * 100)}% confidence`}
              size="small"
              sx={{
                backgroundColor: confidence > 0.8 ? '#e8f5e9' : confidence > 0.5 ? '#fff3e0' : '#ffebee',
                color: confidence > 0.8 ? '#2e7d32' : confidence > 0.5 ? '#e65100' : '#c62828',
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            />
          </Box>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: 1.5,
          }}>
            {fields.map(({ key, label }) => {
              const value = extractedData[key];
              if (!value) return null;
              return (
                <Box key={key}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {value}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
        {confidence < 0.7 && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            Low confidence score. Please verify the extracted data before applying.
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onClick={handleOpenDialog}
        startIcon={<DocumentScannerIcon />}
        disabled={disabled}
        sx={{ textTransform: 'none' }}
      >
        {buttonLabel || defaultLabel}
      </Button>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 2,
          px: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DocumentScannerIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {documentType === 'drivers_license' 
                ? 'Scan Driver\'s License' 
                : 'Scan Insurance Card'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, backgroundColor: '#fafbfc' }}>
          {!extractedData && (
            <>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                  backgroundColor: 'white',
                  mb: 2,
                }}
              >
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#1976d2',
                      height: 3,
                    },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      py: 1.5,
                      '&.Mui-selected': {
                        color: '#1976d2',
                        fontWeight: 600,
                      },
                    },
                  }}
                >
                  <Tab 
                    icon={<UploadFileIcon sx={{ fontSize: 20 }} />} 
                    label="Upload" 
                    iconPosition="start"
                  />
                  <Tab 
                    icon={<CameraAltIcon sx={{ fontSize: 20 }} />} 
                    label="Camera" 
                    iconPosition="start"
                  />
                </Tabs>

                {/* Upload Tab */}
                {activeTab === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4, px: 3 }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => fileInputRef.current?.click()}
                      startIcon={<UploadFileIcon />}
                      sx={{ 
                        mb: 2,
                        px: 4,
                        py: 1.25,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        backgroundColor: '#1976d2',
                        '&:hover': { backgroundColor: '#1565c0' },
                      }}
                    >
                      Select Image
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      Supported formats: JPEG, PNG, WebP (max 10MB)
                    </Typography>
                  </Box>
                )}

                {/* Camera Tab */}
                {activeTab === 1 && (
                  <Box sx={{ textAlign: 'center', py: 3, px: 3 }}>
                    <Box sx={{ 
                      position: 'relative',
                      width: '100%',
                      maxWidth: 400,
                      mx: 'auto',
                      mb: 2,
                      backgroundColor: '#1a1a1a',
                      borderRadius: 2,
                      overflow: 'hidden',
                      minHeight: 200,
                    }}>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{ 
                          width: '100%', 
                          display: imagePreview && activeTab === 1 ? 'none' : 'block' 
                        }}
                      />
                      {imagePreview && activeTab === 1 && (
                        <img 
                          src={imagePreview} 
                          alt="Captured" 
                          style={{ width: '100%' }}
                        />
                      )}
                    </Box>
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    {!imagePreview ? (
                      <Button
                        variant="contained"
                        onClick={capturePhoto}
                        startIcon={<CameraAltIcon />}
                        sx={{ 
                          px: 4,
                          py: 1.25,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          backgroundColor: '#1976d2',
                          '&:hover': { backgroundColor: '#1565c0' },
                        }}
                      >
                        Capture Photo
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setImagePreview(null);
                          setSelectedImage(null);
                        }}
                        sx={{ 
                          px: 4,
                          py: 1.25,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Retake Photo
                      </Button>
                    )}
                  </Box>
                )}
              </Paper>

              {/* Image Preview for Upload */}
              {imagePreview && activeTab === 0 && (
                <Paper 
                  elevation={0}
                  sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="Selected document"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 280,
                      borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  />
                </Paper>
              )}
            </>
          )}

          {/* Extracted Data Preview */}
          {renderDataPreview()}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#fafbfc', borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleCloseDialog} 
            sx={{ 
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          {!extractedData ? (
            <Button
              variant="contained"
              onClick={handleProcess}
              disabled={!selectedImage || isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <DocumentScannerIcon />}
              sx={{ 
                px: 3,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                },
              }}
            >
              {isProcessing ? 'Processing...' : 'Process Document'}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setExtractedData(null);
                  setConfidence(null);
                  setSelectedImage(null);
                  setImagePreview(null);
                }}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Scan Again
              </Button>
              <Button
                variant="contained"
                onClick={handleApply}
                startIcon={<CheckCircleIcon />}
                sx={{ 
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#4caf50',
                  '&:hover': { backgroundColor: '#43a047' },
                }}
              >
                Apply Data
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentScanButton;
